import { api } from '@api'
import type { Profile, ProfilePrivate } from '@hvf/contracts'
import * as Sentry from '@sentry/solid'
import type { Session } from '@supabase/supabase-js'
import { createEffect, createResource, createSignal } from 'solid-js'
import { fetchImageUrl } from '../../features/Avatar/fetchImageUrl'
import { loginUserThirdParty } from './loginUserThirdParty'

export const [user, setUser] = createSignal<Session>()
export const isAuthenticated = () => !!user()?.access_token
export const userId = () => user()?.user.id

export function useAuth() {
  const [profile, setProfile] = createSignal<Profile>()
  const [profilePrivate, setProfilePrivate] = createSignal<ProfilePrivate>()

  const [upsertProfile, setUpsertProfile] = createSignal<Profile>()
  const [upsertProfileResource] = createResource(upsertProfile, setUpsertProfile)

  const [profileResource, { refetch: refetchProfile }] = createResource(
    user,
    async (currentUser) => {
      if (currentUser) {
        const { data: publicProfile, error } = await api()
          .from('profile')
          .select('*')
          .eq('id', currentUser.user.id)
          .single()

        const { data: privateProfile, error: privateProfileError } = await api()
          .from('profile_private')
          .select('*')
          .eq('id', currentUser.user.id)
          .single()
        try {
          loginUserThirdParty(currentUser.user.id, currentUser.user.email)
        } catch (error) {
          console.error('error setting user context for third parties: ', error)
        }
        return { privateProfile, publicProfile }
      }
    },
  )

  const username = () => profile()?.username ?? undefined
  const isRecruit = () => profilePrivate()?.role === 'recruit'
  const isRifleman = () => profilePrivate()?.role === 'rifleman'
  const isCorporal = () => profilePrivate()?.role === 'corporal'
  const isSergeant = () => profilePrivate()?.role === 'sergeant'
  const isAdmin = () => profilePrivate()?.role === 'admin'
  const isCommander = () => profilePrivate()?.role === 'commander'
  const isSuper = () => isAdmin() || isCommander()
  const isOnboarded = () => username() && profilePictureResource()
  const canActionPhq = () => isCommander() || isAdmin() || profilePrivate()?.is_top_gun
  const canSendAudio = () => isCommander() || isAdmin()
  const canAccessScanTool = () => {

  }

  const logout = async () => {
    try {
      const { error } = await api().auth.signOut()
      if (error) {
        console.warn('Logout warning:', error.message)
      }
    } catch (err) {
      console.warn('Logout exception (ignoring):', err)
    } finally {
      Sentry.setUser(null)
      localStorage.removeItem('sb-127-auth-token')
      setUser()
    }
  }

  // Use the unified fetchImageUrl instead of getProfilePicture
  const [profilePictureResource] = createResource(userId, fetchImageUrl)

  createEffect(() => {
    const res = profileResource()
    res?.publicProfile && setProfile(res.publicProfile)
    res?.privateProfile && setProfilePrivate(res.privateProfile)
  })

  return {
    canActionPhq,
    isAdmin,
    isSuper,
    isCommander,
    isCorporal,
    isOnboarded,
    isRecruit,
    isRifleman,
    isSergeant,
    logout,
    profile,
    profilePictureResource,
    profileResource,
    refetchProfile,
    setProfile,
    setUpsertProfile,
    upsertProfileResource,
    username,
    profilePrivate,
    canSendAudio
  }
}
